import { useState, ReactNode } from 'react';
import { DataTable, DataTableColumn, DataTableAction, DataTableFilter, MassAction } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Plus, Edit, Trash2, Eye, Loader2 } from 'lucide-react';

export interface CrudTableColumn<T> extends DataTableColumn<T> {}

export interface CrudTableProps<T extends Record<string, any>> {
  // Data
  data: T[];
  keyField: keyof T;
  columns: CrudTableColumn<T>[];
  tableName: string;
  singularName: string;
  
  // Permissions
  canCreate?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
  canView?: boolean;
  
  // Search & Filter
  searchPlaceholder?: string;
  searchFields?: (keyof T | string)[];
  filters?: DataTableFilter[];
  
  // Custom actions
  extraActions?: DataTableAction<T>[];
  extraMassActions?: MassAction<T>[];
  
  // Form rendering
  renderForm?: (item: T | null, onClose: () => void, onSuccess: () => void) => ReactNode;
  
  // Detail view
  renderDetail?: (item: T, onClose: () => void) => ReactNode;
  onRowClick?: (item: T) => void;
  
  // Callbacks
  onRefresh: () => void;
  onDelete?: (item: T) => Promise<void>;
  onBulkDelete?: (items: T[]) => Promise<void>;
  
  // Display
  titleField?: keyof T | string;
  subtitleField?: keyof T | string;
  emptyMessage?: string;
  emptyIcon?: ReactNode;
  loading?: boolean;
  enableViewToggle?: boolean;
  defaultView?: 'list' | 'card';
  cardRender?: (row: T, actions: DataTableAction<T>[]) => ReactNode;
  
  // Header
  title?: string;
  description?: string;
  headerActions?: ReactNode;
  createButtonLabel?: string;
}

export function CrudTable<T extends Record<string, any>>({
  data,
  keyField,
  columns,
  tableName,
  singularName,
  canCreate = true,
  canUpdate = true,
  canDelete = true,
  canView = true,
  searchPlaceholder,
  searchFields = [],
  filters = [],
  extraActions = [],
  extraMassActions = [],
  renderForm,
  renderDetail,
  onRowClick,
  onRefresh,
  onDelete,
  onBulkDelete,
  titleField,
  subtitleField,
  emptyMessage,
  emptyIcon,
  loading = false,
  enableViewToggle = true,
  defaultView = 'list',
  cardRender,
  title,
  description,
  headerActions,
  createButtonLabel,
}: CrudTableProps<T>) {
  const { toast } = useToast();
  const [formOpen, setFormOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<T | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailItem, setDetailItem] = useState<T | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<T | null>(null);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [itemsToDelete, setItemsToDelete] = useState<T[]>([]);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Handlers
  const handleCreate = () => {
    setSelectedItem(null);
    setFormOpen(true);
  };

  const handleEdit = (item: T) => {
    setSelectedItem(item);
    setFormOpen(true);
  };

  const handleView = (item: T) => {
    if (renderDetail) {
      setDetailItem(item);
      setDetailOpen(true);
    } else if (onRowClick) {
      onRowClick(item);
    }
  };

  const handleDeleteClick = (item: T) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleBulkDeleteClick = (items: T[]) => {
    setItemsToDelete(items);
    setBulkDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setDeleteLoading(true);
    
    try {
      if (onDelete) {
        await onDelete(itemToDelete);
      } else {
        const { error } = await supabase
          .from(tableName)
          .delete()
          .eq(String(keyField), itemToDelete[keyField]);
        
        if (error) throw error;
      }
      
      toast({ title: `${singularName} deleted successfully` });
      onRefresh();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleBulkDeleteConfirm = async () => {
    if (itemsToDelete.length === 0) return;
    setDeleteLoading(true);
    
    try {
      if (onBulkDelete) {
        await onBulkDelete(itemsToDelete);
      } else {
        const ids = itemsToDelete.map(item => item[keyField]);
        const { error } = await supabase
          .from(tableName)
          .delete()
          .in(String(keyField), ids);
        
        if (error) throw error;
      }
      
      toast({ title: `${itemsToDelete.length} ${singularName.toLowerCase()}(s) deleted successfully` });
      onRefresh();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setDeleteLoading(false);
      setBulkDeleteDialogOpen(false);
      setItemsToDelete([]);
    }
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setSelectedItem(null);
  };

  const handleFormSuccess = () => {
    setFormOpen(false);
    setSelectedItem(null);
    onRefresh();
  };

  // Build actions
  const actions: DataTableAction<T>[] = [
    ...(canView && renderDetail ? [{
      label: 'View',
      icon: <Eye className="h-4 w-4" />,
      onClick: handleView,
    }] : []),
    ...(canUpdate && renderForm ? [{
      label: 'Edit',
      icon: <Edit className="h-4 w-4" />,
      onClick: handleEdit,
    }] : []),
    ...extraActions,
    ...(canDelete ? [{
      label: 'Delete',
      icon: <Trash2 className="h-4 w-4" />,
      onClick: handleDeleteClick,
      variant: 'destructive' as const,
    }] : []),
  ];

  // Build mass actions
  const massActions: MassAction<T>[] = [
    ...extraMassActions,
    ...(canDelete ? [{
      label: 'Delete Selected',
      icon: <Trash2 className="h-4 w-4 mr-2" />,
      onClick: handleBulkDeleteClick,
      variant: 'destructive' as const,
    }] : []),
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      {(title || headerActions || (canCreate && renderForm)) && (
        <div className="flex items-center justify-between">
          <div>
            {title && <h1 className="text-3xl font-bold mb-2">{title}</h1>}
            {description && <p className="text-muted-foreground">{description}</p>}
          </div>
          <div className="flex items-center gap-3">
            {headerActions}
            {canCreate && renderForm && (
              <Button onClick={handleCreate}>
                <Plus className="mr-2 h-4 w-4" />
                {createButtonLabel || `Add ${singularName}`}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Data Table */}
      <DataTable
        data={data}
        columns={columns}
        keyField={keyField}
        searchPlaceholder={searchPlaceholder || `Search ${singularName.toLowerCase()}s...`}
        searchFields={searchFields}
        filters={filters}
        actions={actions}
        massActions={massActions}
        onRowClick={onRowClick || (renderDetail ? handleView : undefined)}
        titleField={titleField}
        subtitleField={subtitleField}
        emptyMessage={emptyMessage || `No ${singularName.toLowerCase()}s found`}
        emptyIcon={emptyIcon}
        loading={loading}
        enableViewToggle={enableViewToggle}
        defaultView={defaultView}
        cardRender={cardRender}
      />

      {/* Form Dialog */}
      {renderForm && formOpen && (
        <Dialog open={formOpen} onOpenChange={setFormOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {selectedItem ? `Edit ${singularName}` : `Add ${singularName}`}
              </DialogTitle>
            </DialogHeader>
            {renderForm(selectedItem, handleFormClose, handleFormSuccess)}
          </DialogContent>
        </Dialog>
      )}

      {/* Detail Dialog */}
      {renderDetail && detailOpen && detailItem && (
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            {renderDetail(detailItem, () => setDetailOpen(false))}
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {singularName}</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this {singularName.toLowerCase()}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleteLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {itemsToDelete.length} {singularName}(s)</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {itemsToDelete.length} {singularName.toLowerCase()}(s)? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDeleteConfirm}
              disabled={deleteLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                `Delete ${itemsToDelete.length} Item(s)`
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default CrudTable;
